import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { influencerMemberService } from '@/services/influencerMemberService';
import { InfluencerUser } from '@/types';
import { UserPlus, Mail, Trash2, Edit, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '@/hooks/useAuth';

interface InviteForm {
   email: string;
   firstName: string;
   lastName: string;
   socialMediaHandle?: string;
   platform?: string;
}

interface RoleChangeForm {
   role: 'influencer' | 'admin' | 'super admin';
}

export const InfluencerMembersPage = () => {
   const [members, setMembers] = useState<InfluencerUser[]>([]);
   const [loading, setLoading] = useState(true);
   const [showInviteModal, setShowInviteModal] = useState(false);
   const [showRoleModal, setShowRoleModal] = useState(false);
   const [selectedMember, setSelectedMember] = useState<InfluencerUser | null>(null);
   const [isInviting, setIsInviting] = useState(false);
   const [isChangingRole, setIsChangingRole] = useState(false);
   const { user } = useAuth();

   const {
      register,
      handleSubmit,
      reset,
      formState: { errors },
   } = useForm<InviteForm>();

   const {
      register: registerRole,
      handleSubmit: handleSubmitRole,
      reset: resetRole,
      setValue: setRoleValue,
      formState: { errors: roleErrors },
   } = useForm<RoleChangeForm>();

   // Check if current user is super admin or admin
   const canManageInfluencers = ['admin', 'super admin'].includes(user?.role || '');

   // Fetch influencer members
   const fetchMembers = async () => {
      try {
         setLoading(true);
         const { members: influencerMembers } = await influencerMemberService.getMembers();
         setMembers(influencerMembers);
      } catch (error: any) {
         console.error('Failed to fetch influencer members:', error);
         toast.error('Failed to load influencer members');
      } finally {
         setLoading(false);
      }
   };

   useEffect(() => {
      fetchMembers();
   }, []);

   // Handle invite influencer
   const onInvite = async (data: InviteForm) => {
      try {
         setIsInviting(true);
         await influencerMemberService.inviteMember(data);
         toast.success('Influencer invitation sent successfully!');
         setShowInviteModal(false);
         reset();
         fetchMembers(); // Refresh the list
      } catch (error: any) {
         const errorMessage = error.response?.data?.error || 'Failed to send invitation';
         toast.error(errorMessage);
      } finally {
         setIsInviting(false);
      }
   };

   // Handle resend invitation
   const handleResendInvitation = async (userId: string, email: string) => {
      try {
         await influencerMemberService.resendInvitation(userId);
         toast.success(`Invitation resent to ${email}`);
         fetchMembers();
      } catch (error: any) {
         const errorMessage = error.response?.data?.error || 'Failed to resend invitation';
         toast.error(errorMessage);
      }
   };

   // Handle remove member
   const handleRemoveMember = async (userId: string, name: string) => {
      if (!confirm(`Are you sure you want to remove ${name}?`)) {
         return;
      }

      try {
         await influencerMemberService.removeMember(userId);
         toast.success(`${name} has been removed`);
         fetchMembers();
      } catch (error: any) {
         const errorMessage = error.response?.data?.error || 'Failed to remove member';
         toast.error(errorMessage);
      }
   };

   // Handle open role change modal
   const handleOpenRoleModal = (member: InfluencerUser) => {
      setSelectedMember(member);
      setRoleValue('role', member.role);
      setShowRoleModal(true);
   };

   // Handle role change
   const onRoleChange = async (data: RoleChangeForm) => {
      if (!selectedMember) return;

      try {
         setIsChangingRole(true);
         await influencerMemberService.changeRole(selectedMember.id, data.role);
         toast.success(`Role updated to ${data.role}`);
         setShowRoleModal(false);
         resetRole();
         setSelectedMember(null);
         fetchMembers(); // Refresh the list
      } catch (error: any) {
         const errorMessage = error.response?.data?.error || 'Failed to change role';
         toast.error(errorMessage);
      } finally {
         setIsChangingRole(false);
      }
   };

   const getStatusBadge = (status: string) => {
      switch (status) {
         case 'active':
            return (
               <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
                  Active
               </span>
            );
         case 'invited':
            return (
               <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800'>
                  Pending
               </span>
            );
         default:
            return (
               <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'>
                  {status}
               </span>
            );
      }
   };

   const getRoleBadge = (role: string) => {
      switch (role) {
         case 'influencer':
            return (
               <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800'>
                  Influencer
               </span>
            );
         case 'admin':
            return (
               <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
                  Admin
               </span>
            );
         case 'super admin':
            return (
               <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800'>
                  Super Admin
               </span>
            );
         default:
            return (
               <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800'>
                  {role}
               </span>
            );
      }
   };

   return (
      <div className='space-y-6'>
         {/* Header */}
         <div className='flex justify-between items-center'>
            <div>
               <h1 className='text-2xl font-bold text-gray-900'>Influencer Members</h1>
               <p className='mt-1 text-sm text-gray-500'>
                  Manage influencer members and their permissions
               </p>
            </div>
            {canManageInfluencers && (
               <button
                  onClick={() => setShowInviteModal(true)}
                  className='inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
               >
                  <TrendingUp className='h-4 w-4 mr-2' />
                  Invite Influencer
               </button>
            )}
         </div>

         {/* Members List */}
         <div className='bg-white shadow rounded-lg'>
            {loading ? (
               <div className='p-6 text-center'>
                  <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto'></div>
                  <p className='mt-2 text-sm text-gray-500'>Loading influencer members...</p>
               </div>
            ) : members.length === 0 ? (
               <div className='p-6 text-center'>
                  <TrendingUp className='h-12 w-12 text-gray-400 mx-auto' />
                  <h3 className='mt-2 text-sm font-medium text-gray-900'>No influencer members</h3>
                  <p className='mt-1 text-sm text-gray-500'>
                     Get started by inviting your first influencer member.
                  </p>
                  {canManageInfluencers && (
                     <div className='mt-6'>
                        <button
                           onClick={() => setShowInviteModal(true)}
                           className='inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700'
                        >
                           <TrendingUp className='h-4 w-4 mr-2' />
                           Invite Influencer
                        </button>
                     </div>
                  )}
               </div>
            ) : (
               <div className='overflow-hidden'>
                  <table className='min-w-full divide-y divide-gray-200'>
                     <thead className='bg-gray-50'>
                        <tr>
                           <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Member
                           </th>
                           <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Role
                           </th>
                           <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Social Media
                           </th>
                           <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Status
                           </th>
                           <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Last Login
                           </th>
                           <th className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
                              Actions
                           </th>
                        </tr>
                     </thead>
                     <tbody className='bg-white divide-y divide-gray-200'>
                        {members.map((member) => (
                           <tr key={member.id} className='hover:bg-gray-50'>
                              <td className='px-6 py-4 whitespace-nowrap'>
                                 <div className='flex items-center'>
                                    <div className='flex-shrink-0 h-10 w-10'>
                                       <div className='h-10 w-10 bg-gray-200 rounded-full flex items-center justify-center'>
                                          <span className='text-sm font-medium text-gray-700'>
                                             {member.firstName?.charAt(0)}
                                             {member.lastName?.charAt(0)}
                                          </span>
                                       </div>
                                    </div>
                                    <div className='ml-4'>
                                       <div className='text-sm font-medium text-gray-900'>
                                          {member.firstName} {member.lastName}
                                       </div>
                                       <div className='text-sm text-gray-500'>{member.email}</div>
                                    </div>
                                 </div>
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap'>
                                 {getRoleBadge(member.role)}
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap'>
                                 {member.socialMediaHandle ? (
                                    <div className='text-sm text-gray-900'>
                                       @{member.socialMediaHandle}
                                       {member.platform && (
                                          <span className='text-xs text-gray-500 ml-1'>
                                             ({member.platform})
                                          </span>
                                       )}
                                    </div>
                                 ) : (
                                    <span className='text-sm text-gray-500'>Not specified</span>
                                 )}
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap'>
                                 {getStatusBadge(member.status)}
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                                 {member.lastLoginAt
                                    ? new Date(member.lastLoginAt).toLocaleDateString()
                                    : 'Never'}
                              </td>
                              <td className='px-6 py-4 whitespace-nowrap text-sm font-medium'>
                                 <div className='flex items-center space-x-2'>
                                    {member.status === 'invited' && (
                                       <button
                                          onClick={() =>
                                             handleResendInvitation(member.id, member.email)
                                          }
                                          className='text-primary-600 hover:text-primary-700'
                                          title='Resend invitation'
                                       >
                                          <Mail className='h-4 w-4' />
                                       </button>
                                    )}
                                    {canManageInfluencers && member.id !== user?.id && (
                                       <button
                                          onClick={() => handleOpenRoleModal(member)}
                                          className='text-blue-600 hover:text-blue-700'
                                          title='Edit role'
                                       >
                                          <Edit className='h-4 w-4' />
                                       </button>
                                    )}
                                    <button
                                       onClick={() =>
                                          handleRemoveMember(
                                             member.id,
                                             `${member.firstName} ${member.lastName}`
                                          )
                                       }
                                       className='text-red-600 hover:text-red-700'
                                       title='Remove member'
                                    >
                                       <Trash2 className='h-4 w-4' />
                                    </button>
                                 </div>
                              </td>
                           </tr>
                        ))}
                     </tbody>
                  </table>
               </div>
            )}
         </div>

         {/* Invite Modal */}
         {showInviteModal && (
            <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
               <div className='relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white'>
                  <div className='mt-3'>
                     <div className='flex items-center justify-between mb-4'>
                        <h3 className='text-lg font-medium text-gray-900'>Invite Influencer</h3>
                        <button
                           onClick={() => {
                              setShowInviteModal(false);
                              reset();
                           }}
                           className='text-gray-400 hover:text-gray-600'
                        >
                           <svg
                              className='h-6 w-6'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                           >
                              <path
                                 strokeLinecap='round'
                                 strokeLinejoin='round'
                                 strokeWidth={2}
                                 d='M6 18L18 6M6 6l12 12'
                              />
                           </svg>
                        </button>
                     </div>

                     <form onSubmit={handleSubmit(onInvite)} className='space-y-4'>
                        <div>
                           <label
                              htmlFor='email'
                              className='block text-sm font-medium text-gray-700'
                           >
                              Email Address
                           </label>
                           <input
                              {...register('email', {
                                 required: 'Email is required',
                                 pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: 'Invalid email address',
                                 },
                              })}
                              type='email'
                              className={`mt-1 block w-full px-3 py-2 border ${
                                 errors.email ? 'border-red-300' : 'border-gray-300'
                              } rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                              placeholder='influencer@example.com'
                           />
                           {errors.email && (
                              <p className='mt-1 text-sm text-red-600'>{errors.email.message}</p>
                           )}
                        </div>

                        <div className='grid grid-cols-2 gap-4'>
                           <div>
                              <label
                                 htmlFor='firstName'
                                 className='block text-sm font-medium text-gray-700'
                              >
                                 First Name
                              </label>
                              <input
                                 {...register('firstName', { required: 'First name is required' })}
                                 type='text'
                                 className={`mt-1 block w-full px-3 py-2 border ${
                                    errors.firstName ? 'border-red-300' : 'border-gray-300'
                                 } rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                 placeholder='John'
                              />
                              {errors.firstName && (
                                 <p className='mt-1 text-sm text-red-600'>
                                    {errors.firstName.message}
                                 </p>
                              )}
                           </div>

                           <div>
                              <label
                                 htmlFor='lastName'
                                 className='block text-sm font-medium text-gray-700'
                              >
                                 Last Name
                              </label>
                              <input
                                 {...register('lastName', { required: 'Last name is required' })}
                                 type='text'
                                 className={`mt-1 block w-full px-3 py-2 border ${
                                    errors.lastName ? 'border-red-300' : 'border-gray-300'
                                 } rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                                 placeholder='Doe'
                              />
                              {errors.lastName && (
                                 <p className='mt-1 text-sm text-red-600'>
                                    {errors.lastName.message}
                                 </p>
                              )}
                           </div>
                        </div>

                        <div>
                           <label
                              htmlFor='socialMediaHandle'
                              className='block text-sm font-medium text-gray-700'
                           >
                              Social Media Handle (Optional)
                           </label>
                           <input
                              {...register('socialMediaHandle')}
                              type='text'
                              className='mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm'
                              placeholder='johndoe'
                           />
                        </div>

                        <div>
                           <label
                              htmlFor='platform'
                              className='block text-sm font-medium text-gray-700'
                           >
                              Platform (Optional)
                           </label>
                           <select
                              {...register('platform')}
                              className='mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm'
                           >
                              <option value=''>Select a platform</option>
                              <option value='Instagram'>Instagram</option>
                              <option value='TikTok'>TikTok</option>
                              <option value='YouTube'>YouTube</option>
                              <option value='Twitter'>Twitter</option>
                              <option value='LinkedIn'>LinkedIn</option>
                              <option value='Facebook'>Facebook</option>
                              <option value='Other'>Other</option>
                           </select>
                        </div>

                        <div className='flex justify-end space-x-3 pt-4'>
                           <button
                              type='button'
                              onClick={() => {
                                 setShowInviteModal(false);
                                 reset();
                              }}
                              className='px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                           >
                              Cancel
                           </button>
                           <button
                              type='submit'
                              disabled={isInviting}
                              className='px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50'
                           >
                              {isInviting ? 'Sending...' : 'Send Invitation'}
                           </button>
                        </div>
                     </form>
                  </div>
               </div>
            </div>
         )}

         {/* Role Change Modal */}
         {showRoleModal && selectedMember && (
            <div className='fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50'>
               <div className='relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white'>
                  <div className='mt-3'>
                     <div className='flex items-center justify-between mb-4'>
                        <h3 className='text-lg font-medium text-gray-900'>
                           Change Role - {selectedMember.firstName} {selectedMember.lastName}
                        </h3>
                        <button
                           onClick={() => {
                              setShowRoleModal(false);
                              resetRole();
                              setSelectedMember(null);
                           }}
                           className='text-gray-400 hover:text-gray-600'
                        >
                           <svg
                              className='h-6 w-6'
                              fill='none'
                              viewBox='0 0 24 24'
                              stroke='currentColor'
                           >
                              <path
                                 strokeLinecap='round'
                                 strokeLinejoin='round'
                                 strokeWidth={2}
                                 d='M6 18L18 6M6 6l12 12'
                              />
                           </svg>
                        </button>
                     </div>

                     <form onSubmit={handleSubmitRole(onRoleChange)} className='space-y-4'>
                        <div>
                           <label
                              htmlFor='role'
                              className='block text-sm font-medium text-gray-700'
                           >
                              Current Role: {getRoleBadge(selectedMember.role)}
                           </label>
                           <div className='mt-2'>
                              <label className='block text-sm font-medium text-gray-700 mb-2'>
                                 New Role
                              </label>
                              <select
                                 {...registerRole('role', { required: 'Role is required' })}
                                 className={`mt-1 block w-full px-3 py-2 border ${
                                    roleErrors.role ? 'border-red-300' : 'border-gray-300'
                                 } rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
                              >
                                 <option value='influencer'>Influencer</option>
                                 <option value='admin'>Admin</option>
                                 <option value='super admin'>Super Admin</option>
                              </select>
                              {roleErrors.role && (
                                 <p className='mt-1 text-sm text-red-600'>
                                    {roleErrors.role.message}
                                 </p>
                              )}
                           </div>
                        </div>

                        <div className='flex justify-end space-x-3 pt-4'>
                           <button
                              type='button'
                              onClick={() => {
                                 setShowRoleModal(false);
                                 resetRole();
                                 setSelectedMember(null);
                              }}
                              className='px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                           >
                              Cancel
                           </button>
                           <button
                              type='submit'
                              disabled={isChangingRole}
                              className='px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50'
                           >
                              {isChangingRole ? 'Changing...' : 'Change Role'}
                           </button>
                        </div>
                     </form>
                  </div>
               </div>
            </div>
         )}
      </div>
   );
}; 